import { useEffect, useId, useRef, useState, type ReactNode } from 'react';

export interface ComboOption {
  id: number;
  label: string;
  detail?: string;
}

/**
 * A type-to-search picker: the ARIA "combobox with listbox popup" pattern, so it
 * works with a keyboard and a screen reader. The parent owns the search (it
 * calls the server) and the list of matches; this owns the interaction.
 *
 * Arrow keys move through the matches and the footer action, Enter picks,
 * Escape closes. `footer` is an optional last action such as "+ Add new".
 */
export function Combobox(props: {
  label: string;
  /** What is shown in the box: the picked item's name, or what is being typed. */
  text: string;
  onText(text: string): void;
  options: ComboOption[];
  onPick(option: ComboOption): void;
  placeholder?: string;
  footer?: { label: string; onChoose(): void };
  error?: string;
  /** Clears the current pick. Shown when something is picked and this is given. */
  onClear?: () => void;
  hasValue?: boolean;
  busy?: boolean;
  children?: ReactNode;
}) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const box = useRef<HTMLDivElement>(null);
  const count = props.options.length + (props.footer ? 1 : 0);

  useEffect(() => {
    const away = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, []);

  function choose(i: number) {
    if (i < props.options.length) props.onPick(props.options[i]!);
    else props.footer?.onChoose();
    setOpen(false);
    setActive(-1);
  }

  function key(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((a) => (count === 0 ? -1 : (a + 1) % count));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => (count === 0 ? -1 : (a - 1 + count) % count));
    } else if (e.key === 'Enter' && open && active >= 0) {
      e.preventDefault();
      choose(active);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setActive(-1);
    }
  }

  return (
    <div className="combo" ref={box}>
      <input
        role="combobox"
        aria-label={props.label}
        aria-expanded={open}
        aria-controls={`${id}-list`}
        aria-autocomplete="list"
        aria-activedescendant={active >= 0 ? `${id}-o${active}` : undefined}
        aria-invalid={props.error ? true : undefined}
        autoComplete="off"
        placeholder={props.placeholder}
        value={props.text}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          props.onText(e.target.value);
          setOpen(true);
          setActive(-1);
        }}
        onKeyDown={key}
      />
      {props.hasValue && props.onClear && (
        <button
          type="button"
          className="combo-clear"
          aria-label={`Clear ${props.label}`}
          title="Clear"
          onClick={props.onClear}
        >
          <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
            <path
              d="M4 4l8 8M12 4l-8 8"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
            />
          </svg>
        </button>
      )}
      {open && (
        <ul
          id={`${id}-list`}
          role="listbox"
          aria-label={`${props.label} matches`}
          className="combo-list"
        >
          {props.busy && props.options.length === 0 && (
            <li role="presentation" className="combo-note">
              Searching…
            </li>
          )}
          {!props.busy && props.options.length === 0 && (
            <li role="presentation" className="combo-note">
              No matches
            </li>
          )}
          {props.options.map((o, i) => (
            <li
              key={o.id}
              id={`${id}-o${i}`}
              role="option"
              aria-selected={i === active}
              className={i === active ? 'on' : undefined}
              // mousedown, not click: the box loses focus (and closes) before a click lands.
              onMouseDown={(e) => {
                e.preventDefault();
                choose(i);
              }}
            >
              {o.label}
              {o.detail && <span className="muted"> {o.detail}</span>}
            </li>
          ))}
          {props.footer && (
            <li
              id={`${id}-o${props.options.length}`}
              role="option"
              aria-selected={active === props.options.length}
              className={active === props.options.length ? 'on add' : 'add'}
              onMouseDown={(e) => {
                e.preventDefault();
                choose(props.options.length);
              }}
            >
              {props.footer.label}
            </li>
          )}
        </ul>
      )}
      {props.error && <span className="error">{props.error}</span>}
      {props.children}
    </div>
  );
}
